<template>
    <div class="h-full w-full border-gray-200">
        <div v-if="visibleBulkActionMenu" class="h-full w-full">
            <div class="w-full flex items-center space-between space-x-2  bg-white h-[46px] border-b-2 border-gray-300" >
                <div class="pl-2.5">
                    <button
                        @click="hideBulkActionMenu"
                        class="p-2 hover:bg-gray-300"
                    >
                        <BackArrowSVG />
                    </button>
                </div>
                <div class="tracking-wider font-medium  text-gray-700 flex w-full px-2 py-1 pretty justify-start items-center">
                    Bulk {{ bulkAction.label }}
                </div>
            </div>
            <div class="h-[calc(100%-70px)]">
                <!-- h-[calc(100vh-11.5rem)] -->
                <BulkOperationForm
                    :selectedResults="selectedResults"
                    :selectedResultFields="selectedResultFields"
                    :bulkAction="bulkAction"
                    :response="bulkOperationResponse"
                    :fetch-filter="fetchFilter"
                    :width="width"
                    @cancel-bulk-operation="hideBulkActionMenu"
                    @clear-selected-results="clearSelectedResults"
                    @refetch-results="refetchResults"
                    @perform-bulk-action="performBulkAction"
                    @handle-bulk-operation-input-change="handleBulkOperationInputChange"
                />
                <!-- <slot name="bulk-operations"
                    :selectedResults="selectedResults"
                    :bulkAction="bulkAction"
                    :events="{ hideBulkActionMenu }"
                >
                </slot> -->
            </div>
        </div>
        <div v-else class="flex flex-col w-full h-full">
            <div class="py-1.5  px-3 bg-white flex flex-col justify-center items-center border-r-2 z-20 border-b-2  border-gray-200 fixed"
            :class="width"
            >
                <CustomTabBar
                    class=" w-full justify-between space-x-3 pr-[22px]"
                    :tabList="bulkOperationTabs"
                    @set-tab="setBulkOperationTab"
                />
                <QuickSearch 
                    class="pt-1 w-full  border-t"
                    placeholder="Search bulk operations"
                    @quick-search="quickSearch"
                    @clear-quick-search="clearQuickSearch"
                />
            </div>
            <div class="h-[calc(100%-7.5rem)] p-3 overflow-y-scroll top-[88px] relative font-medium tracking-wide"  @dragover.prevent="handleDragOver">
                <TransitionGroup
                    tag="ul"
                    name="bulk-operations"
                    v-if="Object.keys(visibleBulkOperations).length > 0"
                >
                    <BulkOperationListElement
                        class="draggableRow"
                        v-for="(bulkOperation, key) in visibleBulkOperations"
                        :key="key"
                        :bulk-operation-key="key"
                        :bulk-operation="bulkOperation"
                        @show-bulk-action-menu="showBulkActionMenu"
                    >
                        <template v-slot:button>
                            <div class="px-1 h-10 py-1.5 flex items-center hover:bg-gray-200 cursor-pointer z-10">
                                <PinFillSVG v-if="bulkOperation.pinned" @click="togglePinnedBulkOperation(key)" class="w-3.5 h-3.5 cursor-pointer" color="#464866" />  
                                <PinOutlineSVG v-else  @click="togglePinnedBulkOperation(key)" class="w-3.5 h-3.5 cursor-pointer" color="#464866" />  
                            </div>
                        </template>
                        <template v-slot:dragButton>
                            <div 
                                v-if="showDraggableBulkOperations" 
                                draggable="true" 
                                @dragstart="handleDragStart($event, key)"
                                @dragend="handleDragEnd"
                                :data-bulk-operation-key="key"
                                class="flex h-10 px-1 items-center hover:bg-gray-200 hover:cursor-grab active:cursor-grabbing"
                                :class="{ 'is-dragging': key === draggedBulkOperationKey}"
                            >
                                <VerticalDragSVG />
                            </div>
                        </template>
                    </BulkOperationListElement>   
                    <li class="h-40" :key="-1"></li> 
                </TransitionGroup>
                <div v-else class="flex w-full justify-center items-center text-center font-normal text-gray-700  pt-4">
                    No Matching Bulk Operations
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, reactive, defineProps, defineEmits, computed } from 'vue';
import QuickSearch from './QuickSearch.vue'
import CustomTabBar from './CustomTabBar.vue'
import BackArrowSVG from '@/components/svg-components/BackArrowSVG.vue';
import BulkOperationListElement from './BulkOperationListElement.vue';
import BulkOperationForm from './BulkOperationForm.vue';
import PinFillSVG from '../svg-components/PinFillSVG.vue'
import PinOutlineSVG from '../svg-components/PinOutlineSVG.vue'
import VerticalDragSVG from '../svg-components/VerticalDragSVG.vue';

const props = defineProps([
    'selectedResults', 
    'selectedResultFields',
    'bulkOperations',
    'title',
    'width',
    'bulkOperationResponse',
    'fetchFilter',
    'reorderBulkOperations'
])

const emit = defineEmits([
    'close',
    'refetch-results',
    'clear-selected-results',
    'perform-bulk-action',
    'toggle-pinned',
    'handle-bulk-operation-input-change',
    'save-bulk-operation-order',
    'set-bulk-operation-options'
])

const bulkOperations = reactive(props.bulkOperations)

const bulkOperationTabs = reactive({
    all: {
        label: 'All Bulk Operations',
        isSelected: false,
        handleTabSelect: setBulkOperationDisplayType.bind(null, 'all'),
        class: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-gray-600',
        selectedClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-dark-primary underline',
        highlightClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full',
        unhighlightClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full',
    },
    pinned: {
        label: 'Pinned',
        isSelected: true,
        handleTabSelect: setBulkOperationDisplayType.bind(null, 'pinned'),
        class: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-gray-600',
        selectedClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-dark-primary underline',
        highlightClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full',
        unhighlightClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full',
    },
})

const bulkOperationDisplayType = ref('pinned')

const visibleBulkActionMenu = ref(false)

const visibleBulkOperations = reactive({})

const quickSearchFilter = ref(null)

const draggedBulkOperationKey = ref(null)

const bulkAction = reactive({
    key: null,
    label: null,
    message: null,
    modelPath: null,
    endpoint: null,
    requireSelectedResults: null,
    payloadType: null,
    inputs: {},
    overrideAction: null,
    limit: null,
})

const showDraggableBulkOperations = computed(() => {
    return bulkOperationDisplayType.value == 'pinned';
})


function setBulkOperationTab(tabKey) {
    clearAllTabs()
    bulkOperationTabs[tabKey].isSelected = true;
    setBulkOperationDisplayType(tabKey)
}

function clearAllTabs() {
    for(const tab of Object.values(bulkOperationTabs)) {
        tab.isSelected = false
    }
}

// ===========
function handleDragStart(event, key) {
    draggedBulkOperationKey.value = key;
    event.dataTransfer.setData('text/plain', key);
}

function handleDragOver(event) {
    event.preventDefault()
    if (!draggedBulkOperationKey.value) return;
    const targetRow = event.target.closest('[data-bulk-operation-key]')
    if (!targetRow) return;
    const targetKey = targetRow.dataset.bulkOperationKey;
    if(draggedBulkOperationKey.value === targetKey) return;
    
    reorderBulkOperationOrdinals(draggedBulkOperationKey.value, targetKey)
}

function handleDragEnd () {
    draggedBulkOperationKey.value = null;
    emitSaveBulkOperationOrder()
    setVisibleBulkOperations()
    // draggingIndex.value = -1;
}

function reorderBulkOperationOrdinals(draggedKey, targetKey) {
    const draggedBulkOperation = bulkOperations[draggedKey]
    const targetBulkOperation = bulkOperations[targetKey]

    if(!draggedBulkOperation || !targetBulkOperation) return;

    const originalOrdinal = draggedBulkOperation.ordinal;
    const targetOrdinal = targetBulkOperation.ordinal;

    if (originalOrdinal === targetOrdinal) return;

    for (const key in bulkOperations) {
        const filter = bulkOperations[key]
        if(key === draggedKey) {
            filter.ordinal = targetOrdinal;
        } else if(originalOrdinal < targetOrdinal) {
            if(filter.ordinal > originalOrdinal && filter.ordinal <= targetOrdinal) {
                filter.ordinal--;
            }
        } else {
            if(filter.ordinal < originalOrdinal && filter.ordinal >= targetOrdinal) {
                filter.ordinal++;
            }
        }
    }
}

function emitSaveBulkOperationOrder() {
    emit('save-bulk-operation-order')
}

// ===========

function setBulkOperationDisplayType(displayType) {
    bulkOperationDisplayType.value = displayType
}

watch(() => quickSearchFilter.value, () => {
    setVisibleBulkOperations()
})

watch(() => bulkOperationDisplayType.value, () => {
    setVisibleBulkOperations()
})


watch(() => props.reorderBulkOperations, () => {
    setVisibleBulkOperations()
})

function clearBulkOperations() {
    // console.log('clearBulkOperations')
    for (let visibleBulkOperation in visibleBulkOperations) delete visibleBulkOperations[visibleBulkOperation];
}

function setVisibleBulkOperations() {
    clearBulkOperations()
    // console.log('setVisibleBulkOperations', quickSearchFilter.value)
    if(bulkOperationDisplayType.value=='all') {
        // console.log('displayType', bulkOperationDisplayType.value)
        if(quickSearchFilter.value && quickSearchFilter.value != '') {
            Object.entries(bulkOperations).sort(([, a], [, b]) => { return a.label.localeCompare(b.label)}).forEach(([bulkOperationKey, bulkOperationValue]) => {
                // console.log(bulkOperationKey, bulkOperationValue)
                if(bulkOperationKey.toLowerCase().includes(quickSearchFilter.value) || bulkOperationValue.label.toLowerCase().includes(quickSearchFilter.value) || bulkOperationValue.endpoint.toLowerCase().includes(quickSearchFilter.value)
                ) {
                    visibleBulkOperations[bulkOperationKey] = bulkOperationValue
                } else {
                    if(!bulkOperationValue.pinned) {
                        delete visibleBulkOperations[bulkOperationKey]
                    }
                }
            })
        } else {
            // Object.entries(bulkOperations).forEach(([bulkOperationKey, bulkOperationValue]) => {
            //     if(bulkOperationValue.pinned) {
            //         visibleBulkOperations[bulkOperationKey] = bulkOperationValue
            //     } else {
            //         delete visibleBulkOperations[bulkOperationKey]
            //     }
            // })
            Object.entries(bulkOperations).sort(([, a], [, b]) => { return a.label.localeCompare(b.label)}).forEach(([bulkOperationKey, bulkOperationValue]) => {
                visibleBulkOperations[bulkOperationKey] = bulkOperationValue
            })
        }
    } else if (bulkOperationDisplayType.value == 'pinned') {
        if(quickSearchFilter.value && quickSearchFilter.value != '') {
            Object.entries(bulkOperations).filter(([, bulkOperation]) => {return bulkOperation.persisted_id}).sort(([, a], [, b]) => { return a.ordinal - b.ordinal}).forEach(([bulkOperationKey, bulkOperationValue]) => {
                // console.log(bulkOperationKey, bulkOperationValue)
                if(bulkOperationValue.pinned) {
                    if(bulkOperationKey.toLowerCase().includes(quickSearchFilter.value) || bulkOperationValue.label.toLowerCase().includes(quickSearchFilter.value) || bulkOperationValue.endpoint.toLowerCase().includes(quickSearchFilter.value)
                    ) {
                        visibleBulkOperations[bulkOperationKey] = bulkOperationValue
                    }
                } else {
                    delete visibleBulkOperations[bulkOperationKey]
                }
            })
        } else {
            Object.entries(bulkOperations).filter(([, bulkOperation]) => {return bulkOperation.persisted_id}).sort(([, a], [, b]) => { return a.ordinal - b.ordinal}).forEach(([bulkOperationKey, bulkOperationValue]) => {
                // console.log(bulkOperationKey, bulkOperationValue.persisted_id)
                if(bulkOperationValue.pinned) {
                    visibleBulkOperations[bulkOperationKey] = bulkOperationValue
                }
            })
        }
    }
}


function quickSearch(filterValue) {
    // console.log('quickSearch', filterValue)
    if(filterValue) {
        quickSearchFilter.value = filterValue.toLowerCase()
    } else {
        quickSearchFilter.value = null
    }
}

function clearQuickSearch() {
    // console.log('clearQuickSearch')
    quickSearchFilter.value = null
}

function showBulkActionMenu(selectedBulkOperationKey, selectedBulkOperation) {
    visibleBulkActionMenu.value = true
    bulkAction.key = selectedBulkOperationKey
    bulkAction.label = selectedBulkOperation.label
    bulkAction.message = selectedBulkOperation.message
    bulkAction.modelPath = selectedBulkOperation.modelPath
    bulkAction.endpoint = selectedBulkOperation.endpoint
    bulkAction.requireSelectedResults = selectedBulkOperation.requireSelectedResults
    bulkAction.payloadType = selectedBulkOperation.payloadType
    bulkAction.inputs = selectedBulkOperation.inputs
    bulkAction.overrideAction = selectedBulkOperation.overrideAction ? selectedBulkOperation.overrideAction : null
    bulkAction.limit = selectedBulkOperation.limit ? selectedBulkOperation.limit : null
    emit('set-bulk-operation-options', selectedBulkOperationKey)
}

function hideBulkActionMenu() {
    // console.log('hideBulkActionMenu')
    visibleBulkActionMenu.value = false
    bulkAction.key = null
    bulkAction.label = null
    bulkAction.message = null
    bulkAction.modelPath = null
    bulkAction.endpoint = null
    bulkAction.requireSelectedResults = null
    bulkAction.payloadType = null
    bulkAction.inputs = {}
    bulkAction.overrideAction = null
    bulkAction.limit = null
    resetBulkOperationDefaults()
}

function resetBulkOperationDefaults() {
    // console.log('resetBulkOperationDefaults')
    Object.values(bulkOperations).forEach((bulkOperation) => {
        if(bulkOperation.inputs && Object.keys(bulkOperation.inputs).length > 0) {
            Object.values(bulkOperation.inputs).forEach((inputValue) => {
                if(Array.isArray(inputValue.value)) {
                    inputValue.value = []
                } else {
                    inputValue.value = inputValue.defaultValue
                }
            })
        }
    })
}

function togglePinnedBulkOperation(key) {
    // console.log('togglePinnedBulkOperation', key)
    // bulkOperations[key].pinned = !bulkOperations[key].pinned
    // setVisibleBulkOperations()
    emit('toggle-pinned', key)
}

function refetchResults() {
    emit('refetch-results')
}

function clearSelectedResults() {
    emit('clear-selected-results')
}

function performBulkAction(bulkActionData) {
    // console.log('BulkOperationList: performBulkAction')
    // console.log(bulkActionData)
    emit('perform-bulk-action', bulkActionData)
}

function handleBulkOperationInputChange(bulkAction, inputKey, inputValue) {
    emit('handle-bulk-operation-input-change', bulkAction, inputKey, inputValue)
}

// setVisibleBulkOperations()
</script>

<style scoped>
.bulk-operations-move,
.bulk-operations-enter-active {
    transition: all 0.3s ease;
}
.bulk-operations-leave-active {
  transition: all 0.3s ease;
}
.bulk-operations-enter-from,
.bulk-operations-leave-to {
  opacity: 0;
  /* transform: translateX(-30px); */
}

.bulk-operations-leave-active {
    position: absolute;
    /* position: relative; */
}
</style>